// Shared centered-dialog primitive: icon badge, title, body, one or two
// buttons. Replaces the hand-rolled confirm/success/error modals that used
// to be reimplemented per call site (MyLineup's SuccessModal,
// ConfirmCutModal, the delete/success/error modals in LeagueRules).
import React from 'react';
import { ModalPortal, useLockBodyScroll } from './ModalPortal';

const ICON_TONES = {
  danger: { bg: 'var(--color-danger-bg)', color: 'var(--color-danger)' },
  success: { bg: 'var(--color-success-bg)', color: 'var(--color-success)' },
  warning: { bg: 'var(--color-captain-fill)', color: 'var(--color-warning)' },
  brand: { bg: 'var(--color-band-bg)', color: 'var(--color-brand)' },
};

const BUTTON_TONES = {
  danger: { background: 'var(--color-danger)', color: 'var(--color-bg-card)' },
  brand: { background: 'var(--color-brand)', color: 'var(--color-bg-card)' },
};

export default function CenteredModal({
  open,
  onClose,
  icon: Icon,
  iconTone = 'brand',
  title,
  children,
  primaryLabel,
  onPrimary,
  primaryTone = 'brand',
  primaryLoading = false,
  primaryLoadingLabel,
  secondaryLabel = 'Cancel',
  onSecondary,
}) {
  useLockBodyScroll(open);
  if (!open) return null;

  const tone = ICON_TONES[iconTone] || ICON_TONES.brand;
  const primaryStyle = BUTTON_TONES[primaryTone] || BUTTON_TONES.brand;

  return (
    <ModalPortal open={open}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{ position: 'fixed', inset: 0, zIndex: 10000 }}
      >
        <div
          onClick={onClose}
          style={{ position: 'absolute', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)' }}
        />
        <div
          style={{
            position: 'relative', height: '100%', width: '100%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
            paddingTop: 'env(safe-area-inset-top)',
            paddingBottom: 'env(safe-area-inset-bottom)',
          }}
        >
          <div
            style={{
              backgroundColor: 'var(--color-bg-card)', borderRadius: 20, padding: 24,
              maxWidth: 380, width: '100%', textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            }}
          >
            {Icon && (
              <div
                style={{
                  width: 56, height: 56, borderRadius: '50%',
                  backgroundColor: tone.bg, color: tone.color,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 16px',
                }}
              >
                <Icon size={26} strokeWidth={2.25} />
              </div>
            )}
            {title && (
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text-strong)', margin: '0 0 8px' }}>
                {title}
              </h3>
            )}
            {children && (
              <div style={{ fontSize: 14, color: 'var(--color-text-muted)', marginBottom: 20 }}>
                {children}
              </div>
            )}
            <div style={{ display: 'flex', gap: 12 }}>
              {primaryLabel && onSecondary && (
                <button
                  onClick={onSecondary}
                  style={{
                    flex: 1, padding: '12px 0', borderRadius: 50, border: 'none',
                    backgroundColor: 'var(--color-bg-page)', color: 'var(--color-text-strong)',
                    fontWeight: 600, fontSize: 15, cursor: 'pointer',
                  }}
                >
                  {secondaryLabel}
                </button>
              )}
              <button
                onClick={onPrimary || onClose}
                disabled={primaryLoading}
                style={{
                  flex: 1, padding: '12px 0', borderRadius: 50, border: 'none',
                  backgroundColor: primaryLoading ? 'var(--color-text-faint)' : primaryStyle.background,
                  color: primaryStyle.color,
                  fontWeight: 700, fontSize: 15,
                  cursor: primaryLoading ? 'default' : 'pointer',
                }}
              >
                {primaryLoading ? (primaryLoadingLabel || 'Working…') : (primaryLabel || 'Got it')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
